import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection,
  getDocs,
  query,
  where,
  getCountFromServer,
  orderBy,
  limit,
  documentId,
  doc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { ref, getDownloadURL, getStorage } from 'firebase/storage';
import { app, auth, db, firebaseConfig } from '../firebase';
import { authFetch } from '../utils/authFetch';
import { backendConfig } from '../config/backendConfig';
import { normalizeArtworkType, resolveArtworkTypeForRow } from '../utils/inferArtworkTypeFromTitle';
import ArtmosphereNavbar from '../components/ArtmosphereNavbar';

function tsMillis(t) {
  if (t === null || t === undefined) return 0;
  if (typeof t === 'number' && Number.isFinite(t)) return t;
  if (typeof t.toMillis === 'function') return t.toMillis();
  if (typeof t.seconds === 'number') return t.seconds * 1000;
  return 0;
}

function formatWhen(t) {
  const ms = tsMillis(t);
  if (!ms) return '—';
  try {
    return new Date(ms).toLocaleString();
  } catch {
    return '—';
  }
}

function withTimeout(promise, ms, label = 'request') {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms)
    ),
  ]);
}

function displayTitle(d) {
  return (
    d.verifiedTitle ||
    d.title ||
    d.artworkTitle ||
    'Untitled'
  ).trim();
}

function displayArtist(d) {
  return (
    d.verifiedArtist ||
    d.artistName ||
    ''
  ).trim();
}

function primaryImageUrl(d) {
  return (d.verifiedImageUrl || d.imageUrl || d.verifiedImageUrlRemote || '').trim();
}

function displayArtworkType(d) {
  const resolved = resolveArtworkTypeForRow(d);
  if (resolved.type) return resolved.type;
  return 'Uncategorized';
}

function displayLevel(d) {
  return String(
    d.levelType ||
      d.level ||
      ''
  ).trim() || 'Unspecified';
}

/** Picker uses only `verified-artworks-previews/{id}.webp` + Firestore status (see backend scripts). */
function previewPickerBadge(d) {
  const st = String(d.verifiedImageCardPreviewStatus || '').trim().toUpperCase();
  const expected = `verified-artworks-previews/${d.id}.webp`;
  const pathOk = String(d.verifiedImageCardPreviewStoragePath || '').trim() === expected;
  const w = d.verifiedImageCardPreviewWidth;
  const h = d.verifiedImageCardPreviewHeight;
  const dims = w != null && h != null ? `${w}×${h}` : '—';
  if (st === 'READY') return { label: 'READY', cls: 'text-emerald-700', dims };
  if (st === 'FAILED') return { label: 'FAILED', cls: 'text-red-700', dims };
  if (pathOk)
    return { label: 'SET', cls: 'text-amber-800', dims };
  return { label: 'MISSING', cls: 'text-amber-800', dims };
}

function copyToClipboard(text) {
  if (navigator.clipboard?.writeText) {
    void navigator.clipboard.writeText(text);
  }
}

function gsVerifiedArtworksBucket() {
  const fromEnv = (process.env.REACT_APP_VERIFIED_STORAGE_BUCKET || '').trim();
  const name =
    fromEnv ||
    (firebaseConfig?.projectId ? `${firebaseConfig.projectId}-verified-artworks` : 'artmosphere-89b64-verified-artworks');
  return `gs://${name}`;
}

let verifiedStorageSingleton = null;
function getVerifiedStorage() {
  if (!verifiedStorageSingleton) {
    verifiedStorageSingleton = getStorage(app, gsVerifiedArtworksBucket());
  }
  return verifiedStorageSingleton;
}

/** One shared wait for persistence restore after hard refresh — avoids N parallel onAuthStateChanged leaks. */
let whenSignedInSharedPromise = null;

function whenSignedIn(maxMs = 12000) {
  if (auth.currentUser) return Promise.resolve();
  if (!whenSignedInSharedPromise) {
    whenSignedInSharedPromise = new Promise((resolve) => {
      let settled = false;
      const done = () => {
        if (settled) return;
        settled = true;
        whenSignedInSharedPromise = null;
        resolve();
      };
      let timer;
      const unsub = onAuthStateChanged(auth, (u) => {
        if (u) {
          clearTimeout(timer);
          unsub();
          done();
        }
      });
      timer = setTimeout(() => {
        unsub();
        done();
      }, maxMs);
    });
  }
  return whenSignedInSharedPromise;
}

/** Cap parallel getDownloadURL after hard refresh (429 / cold auth + App Check). */
const VERIFIED_STORAGE_MAX_PARALLEL = 12;
let verifiedStorageActive = 0;
const verifiedStorageWaitQueue = [];

function acquireVerifiedStorageSlot() {
  if (verifiedStorageActive < VERIFIED_STORAGE_MAX_PARALLEL) {
    verifiedStorageActive += 1;
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    verifiedStorageWaitQueue.push(resolve);
  }).then(() => {
    verifiedStorageActive += 1;
  });
}

function releaseVerifiedStorageSlot() {
  verifiedStorageActive -= 1;
  const next = verifiedStorageWaitQueue.shift();
  if (next) next();
}

async function warmAuthTokenForStorage(forceRefresh = false) {
  try {
    if (auth.currentUser?.getIdToken) {
      await auth.currentUser.getIdToken(forceRefresh);
    }
  } catch {
    /* still try getDownloadURL */
  }
}

async function getVerifiedObjectDownloadUrl(storagePath) {
  await whenSignedIn(15000);
  const r = ref(getVerifiedStorage(), storagePath);
  const maxAttempts = 5;
  let lastErr;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      await warmAuthTokenForStorage(attempt > 1);
      await acquireVerifiedStorageSlot();
      try {
        return await getDownloadURL(r);
      } finally {
        releaseVerifiedStorageSlot();
      }
    } catch (e) {
      lastErr = e;
      const code = String(e?.code || '');
      const msg = String(e?.message || e || '');
      const retry =
        attempt < maxAttempts &&
        (code === 'storage/unauthorized' ||
          code === 'storage/unauthenticated' ||
          code === 'storage/retry-limit-exceeded' ||
          /network|QUOTA|429|timeout|Failed to fetch|internal/i.test(msg));
      if (!retry) throw e;
      await new Promise((res) => setTimeout(res, 400 * attempt + Math.floor(Math.random() * 150)));
    }
  }
  throw lastErr;
}

function isVerifiedLikeRecord(d) {
  if (d.replacementApplied === true) return true;
  if (d.replacementApplied === 'true') return true;
  const url = typeof d.verifiedImageUrl === 'string' ? d.verifiedImageUrl.trim() : '';
  if (url) return true;
  const sha = typeof d.verifiedImageSha256 === 'string' ? d.verifiedImageSha256.trim() : '';
  if (sha) return true;
  return false;
}

/** Must match `verified-artwork-pipeline/src/config.js` allowedArtworkTypes */
const CARD_ARTWORK_TYPES = [
  'Portrait',
  'Landscape',
  'Still Life',
  'Abstract',
  'Sacred',
  'Genre Scene',
  'Self-Portrait',
  'Architectural',
  'Animal/Birds',
  'Fantasy',
  'Study/Sketch',
  'Miniature',
  'Calligraphy',
  'Sculpture',
  'Interior',
  'Seascape',
  'Floral',
  'Objects',
];
const CARD_LEVEL_OPTIONS = ['Level 1', 'Level 2', 'Level 3', 'Unspecified'];

function needsCategoryVerify(r) {
  return r.artworkCategoryReviewNeeded === true || r.artworkCategoryReviewNeeded === 'true';
}

function needsMovementReview(r) {
  return r.movementReviewNeeded === true || r.movementReviewNeeded === 'true';
}

export default function AdminVerifiedArtworksDashboard() {
  const navigate = useNavigate();
  const [isAllowed, setIsAllowed] = useState(false);
  const [adminCheckDone, setAdminCheckDone] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalReplacementApplied, setTotalReplacementApplied] = useState(null);
  const [loadHint, setLoadHint] = useState('');
  const [apiSource, setApiSource] = useState(null);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [artworkTypeFilter, setArtworkTypeFilter] = useState('all');
  const [levelFilter, setLevelFilter] = useState('all');
  const [savingById, setSavingById] = useState({});
  const [saveErrById, setSaveErrById] = useState({});
  const [deletingById, setDeletingById] = useState({});
  const [deleteErrById, setDeleteErrById] = useState({});

  /** Removes the Firestore document. Does not remove objects from the verified GCS bucket. */
  const deleteArtworkRow = async (row) => {
    if (!row?.id) return { ok: false, cancelled: true };
    if (
      !window.confirm(
        'Permanently delete this artwork from the catalog?\n\nThis removes the Firestore document. Files already stored under verified-artworks/ are not deleted automatically.'
      )
    ) {
      return { ok: false, cancelled: true };
    }
    setDeletingById((prev) => ({ ...prev, [row.id]: true }));
    setDeleteErrById((prev) => ({ ...prev, [row.id]: '' }));
    try {
      await deleteDoc(doc(db, 'artHistoryArtworks', row.id));
      setRows((prev) => prev.filter((r) => r.id !== row.id));
      setSelected((sel) => (sel?.id === row.id ? null : sel));
      return { ok: true };
    } catch (e) {
      const msg = e?.message || 'Delete failed';
      setDeleteErrById((prev) => ({ ...prev, [row.id]: msg }));
      return { ok: false, error: msg };
    } finally {
      setDeletingById((prev) => ({ ...prev, [row.id]: false }));
    }
  };

  const autoSaveCardFields = async (row, nextType, nextLevel) => {
    if (!row?.id) return;
    const resolved = resolveArtworkTypeForRow(row);
    const userSelectedType = normalizeArtworkType(String(nextType || '').trim());
    const type = userSelectedType || resolved.type || '';
    const levelRaw = String(nextLevel || '').trim();
    const level = levelRaw === 'Unspecified' ? '' : levelRaw;
    const uid = auth.currentUser?.uid || null;
    const currentType = displayArtworkType(row);
    const currentLevel = displayLevel(row);
    if (currentType === (type || 'Uncategorized') && currentLevel === (level || 'Unspecified')) return;
    if (type && !CARD_ARTWORK_TYPES.includes(type)) return;

    const patch = {
      levelType: level,
      artworkCategoryReviewNeeded: false,
      artworkTypeReviewNeeded: false,
      artworkTypeTagSource: userSelectedType ? 'admin_manual' : resolved.source === 'name_inferred' ? 'artwork_title' : (row.artworkTypeTagSource || 'admin_manual'),
      artworkTypeReviewedAt: serverTimestamp(),
      artworkTypeReviewedBy: uid,
      updatedAt: serverTimestamp(),
    };
    // For matched/original artworks that already had type, keep existing category.
    // Only write inferred type when existing category is missing.
    if (type) {
      patch.typeOfArtwork = type;
      patch.artworkType = type;
    }

    setSavingById((prev) => ({ ...prev, [row.id]: true }));
    setSaveErrById((prev) => ({ ...prev, [row.id]: '' }));
    setRows((prev) =>
      prev.map((r) =>
        r.id === row.id
          ? {
              ...r,
              ...(type ? { typeOfArtwork: type, artworkType: type } : {}),
              levelType: level,
              artworkCategoryReviewNeeded: false,
              artworkTypeReviewNeeded: false,
              artworkTypeTagSource: userSelectedType ? 'admin_manual' : resolved.source === 'name_inferred' ? 'artwork_title' : (r.artworkTypeTagSource || 'admin_manual'),
            }
          : r
      )
    );
    try {
      await updateDoc(doc(db, 'artHistoryArtworks', row.id), patch);
    } catch (e) {
      setSaveErrById((prev) => ({ ...prev, [row.id]: e?.message || 'Save failed' }));
      // Restore row from latest source row in memory if save fails.
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...row } : r)));
    } finally {
      setSavingById((prev) => ({ ...prev, [row.id]: false }));
    }
  };

  useEffect(() => {
    const user = auth?.currentUser;
    if (!user) {
      setAdminCheckDone(true);
      setIsAllowed(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await withTimeout(
          authFetch(`${backendConfig.baseUrl}/api/admin/check-admin?uid=${user.uid}`),
          12000,
          'admin check'
        );
        if (cancelled) return;
        if (!res.ok) {
          setIsAllowed(false);
          setAdminCheckDone(true);
          return;
        }
        const data = await res.json();
        if (!cancelled) {
          setIsAllowed(!!data.isAdmin);
          setAdminCheckDone(true);
        }
      } catch {
        if (!cancelled) {
          setIsAllowed(false);
          setAdminCheckDone(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [auth?.currentUser?.uid]);

  useEffect(() => {
    if (!isAllowed) return;
    let cancelled = false;
    let loadingFailsafeTimer = null;
    (async () => {
      setLoading(true);
      loadingFailsafeTimer = setTimeout(() => {
        if (!cancelled) {
          setLoadHint((h) =>
            h
              ? `${h} Load took too long; showing partial results.`
              : 'Load took too long; showing partial results.'
          );
          setLoading(false);
        }
      }, 25000);
      setRows([]);
      setLoadHint('');
      setApiSource(null);
      const col = collection(db, 'artHistoryArtworks');
      const byId = new Map();

      const mergeSnap = (snap) => {
        snap.docs.forEach((docSnap) => {
          const id = docSnap.id;
          const data = docSnap.data();
          const prev = byId.get(id);
          byId.set(id, prev ? { ...prev, ...data, id } : { id, ...data });
        });
      };

      try {
        const cnt = await withTimeout(
          getCountFromServer(query(col, where('replacementApplied', '==', true))),
          12000,
          'replacementApplied count'
        );
        if (!cancelled) setTotalReplacementApplied(cnt.data().count);
      } catch {
        if (!cancelled) setTotalReplacementApplied(null);
      }

      try {
        const s1 = await withTimeout(
          getDocs(query(col, where('replacementApplied', '==', true))),
          18000,
          'replacementApplied query'
        );
        if (cancelled) return;
        mergeSnap(s1);
      } catch (e) {
        console.error('[AdminVerifiedArtworks] replacementApplied query', e);
      }

      try {
        const s2 = await withTimeout(
          getDocs(query(col, where('verifiedImageUrl', '!=', ''), orderBy('verifiedImageUrl'), limit(800))),
          18000,
          'verifiedImageUrl query'
        );
        if (cancelled) return;
        mergeSnap(s2);
      } catch (e) {
        console.warn('[AdminVerifiedArtworks] verifiedImageUrl query:', e?.message || e);
        if (!cancelled) {
          setLoadHint((h) =>
            h
              ? h
              : 'Optional Firestore index: inequality on verifiedImageUrl may be missing — using document scan fallback when needed.'
          );
        }
      }

      let list = Array.from(byId.values());

      if (list.length === 0) {
        try {
          const s3 = await withTimeout(
            getDocs(query(col, orderBy(documentId()), limit(900))),
            18000,
            'fallback scan query'
          );
          if (cancelled) return;
          const filtered = s3.docs
            .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
            .filter(isVerifiedLikeRecord);
          filtered.forEach((row) => byId.set(row.id, row));
          list = Array.from(byId.values());
          if (!cancelled && list.length > 0) {
            setLoadHint(
              'Loaded from a sample scan (first ~900 doc IDs): ensure Firestore has replacementApplied or verifiedImageUrl on records you care about.'
            );
          }
        } catch (e) {
          console.error('[AdminVerifiedArtworks] fallback scan', e);
        }
      }

      try {
        const apiRes = await withTimeout(
          authFetch(`${backendConfig.baseUrl}/api/admin/verified-artworks`),
          12000,
          'admin verified-artworks api'
        );
        if (cancelled) return;
        if (apiRes.ok) {
          const payload = await apiRes.json();
          const items = Array.isArray(payload.items) ? payload.items : [];
          items.forEach((row) => {
            const id = row.id;
            if (!id) return;
            const prev = byId.get(id);
            byId.set(id, prev ? { ...prev, ...row, id } : { ...row, id });
          });
          setApiSource({
            ok: true,
            count: payload.count ?? items.length,
            projectId: payload.projectId || '',
            crossProjectRead: !!payload.crossProjectRead,
            crossReadError: payload.crossReadError || null,
            usedFallbackScan: !!payload.usedFallbackScan,
          });
          if (items.length > 0) {
            setLoadHint((prev) =>
              prev
                ? `${prev} Also merged ${items.length} row(s) from admin API (Firestore ${payload.projectId || 'backend'}).`
                : `Merged ${items.length} row(s) from admin API (Firestore project: ${payload.projectId || 'backend'}).`
            );
          }
        } else {
          const errText = await apiRes.text().catch(() => '');
          setApiSource({
            ok: false,
            status: apiRes.status,
            detail: errText.slice(0, 200),
          });
        }
      } catch (e) {
        if (!cancelled) {
          setApiSource({ ok: false, detail: e?.message || 'Network error' });
          console.warn('[AdminVerifiedArtworks] admin API:', e);
        }
      }

      if (!cancelled) {
        list = Array.from(byId.values());
        list.sort((a, b) => tsMillis(b.replacementAppliedAt) - tsMillis(a.replacementAppliedAt));
        setRows(list);
      }
      if (!cancelled) setLoading(false);
      if (loadingFailsafeTimer) {
        clearTimeout(loadingFailsafeTimer);
        loadingFailsafeTimer = null;
      }
    })();
    return () => {
      cancelled = true;
      if (loadingFailsafeTimer) {
        clearTimeout(loadingFailsafeTimer);
        loadingFailsafeTimer = null;
      }
    };
  }, [isAllowed]);

  const baseFiltered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (!q) return true;
      const blob = [
        displayTitle(r),
        displayArtist(r),
        r.id,
        r.verifiedPlatform,
        r.verifiedSourceUrl,
        r.verifiedLicense,
        r.verifiedRightsClassification || '',
      ]
        .join(' ')
        .toLowerCase();
      return blob.includes(q);
    });
  }, [rows, search]);

  const artworkTypeOptions = useMemo(() => {
    const s = new Set();
    baseFiltered.forEach((r) => s.add(displayArtworkType(r)));
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [baseFiltered]);

  const levelOptions = useMemo(() => {
    const s = new Set();
    baseFiltered.forEach((r) => s.add(displayLevel(r)));
    return Array.from(s).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  }, [baseFiltered]);

  const filtered = useMemo(() => {
    return baseFiltered.filter((r) => {
      if (artworkTypeFilter !== 'all' && displayArtworkType(r) !== artworkTypeFilter) return false;
      if (levelFilter !== 'all' && displayLevel(r) !== levelFilter) return false;
      return true;
    });
  }, [baseFiltered, artworkTypeFilter, levelFilter]);

  const typeLevelMatrix = useMemo(() => {
    const levels = Array.from(
      new Set(baseFiltered.map((r) => displayLevel(r)))
    ).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

    const typeRowsMap = new Map();
    baseFiltered.forEach((r) => {
      const type = displayArtworkType(r);
      const level = displayLevel(r);
      if (!typeRowsMap.has(type)) {
        const init = { type, total: 0 };
        levels.forEach((lv) => { init[lv] = 0; });
        typeRowsMap.set(type, init);
      }
      const row = typeRowsMap.get(type);
      row.total += 1;
      row[level] += 1;
    });

    const typeRows = Array.from(typeRowsMap.values()).sort((a, b) => b.total - a.total);
    return { levels, typeRows };
  }, [baseFiltered]);

  if (!adminCheckDone) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center">
        <p className="text-slate-600">Checking access…</p>
      </div>
    );
  }

  if (!auth?.currentUser) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex flex-col items-center justify-center gap-4">
        <p className="text-slate-700">Sign in to open the admin dashboard.</p>
        <button
          type="button"
          onClick={() => navigate('/login')}
          className="px-4 py-2 rounded-lg bg-amber-600 text-white hover:bg-amber-700"
        >
          Go to login
        </button>
      </div>
    );
  }

  if (!isAllowed) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex flex-col items-center justify-center gap-4 px-4">
        <p className="text-slate-800 text-center max-w-md">
          You do not have admin access. Verified artwork review is restricted to administrators.
        </p>
        <button
          type="button"
          onClick={() => navigate('/my-art-world')}
          className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50"
        >
          Back to app
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-100">
      <ArtmosphereNavbar />
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div>
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="text-sm text-amber-800 hover:text-amber-950 mb-2"
            >
              ← Back
            </button>
            <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900 tracking-tight">
              Verified artworks
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              <span className="font-medium text-slate-700">{filtered.length}</span> artworks shown
            </p>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-4 mb-6">
          <input
            type="search"
            placeholder="Search title, artist, or doc id…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 bg-white shadow-sm focus:ring-2 focus:ring-amber-400/50 focus:border-amber-400 outline-none"
          />
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500 whitespace-nowrap">Artwork type</span>
            <select
              value={artworkTypeFilter}
              onChange={(e) => setArtworkTypeFilter(e.target.value)}
              className="px-4 py-2.5 rounded-xl border border-slate-200 bg-white shadow-sm"
            >
              <option value="all">All artwork types</option>
              {artworkTypeOptions.map((opt) => (
                <option key={`type-opt-${opt}`} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500 whitespace-nowrap">Level</span>
            <select
              value={levelFilter}
              onChange={(e) => setLevelFilter(e.target.value)}
              className="px-4 py-2.5 rounded-xl border border-slate-200 bg-white shadow-sm"
            >
              <option value="all">All levels</option>
              {levelOptions.map((opt) => (
                <option key={`level-opt-${opt}`} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="text-xs text-slate-600 mb-3 space-y-1">
          <p>
            <strong>Picker thumbnails</strong> use only{' '}
            <code className="bg-slate-100 px-1 rounded text-[11px]">GET /api/artwork-preview/&lt;id&gt;.webp</code> (WebP in GCS).
            Run from <code className="bg-slate-100 px-1 rounded">Artmosphere/backend</code>:
          </p>
          <p className="flex flex-wrap gap-2 items-center">
            <button
              type="button"
              className="text-[11px] px-2 py-1 rounded border border-slate-200 bg-white hover:bg-slate-50"
              onClick={() =>
                copyToClipboard('cd Artmosphere/backend && npm run audit:artwork-previews')
              }
            >
              Copy: audit
            </button>
            <button
              type="button"
              className="text-[11px] px-2 py-1 rounded border border-slate-200 bg-white hover:bg-slate-50"
              onClick={() =>
                copyToClipboard('cd Artmosphere/backend && npm run generate:artwork-previews:missing')
              }
            >
              Copy: generate missing
            </button>
            <button
              type="button"
              className="text-[11px] px-2 py-1 rounded border border-slate-200 bg-white hover:bg-slate-50"
              onClick={() =>
                copyToClipboard('cd Artmosphere/backend && npm run generate:artwork-previews:fix')
              }
            >
              Copy: fix bad
            </button>
          </p>
        </div>

        {!loading && typeLevelMatrix.typeRows.length > 0 && (
          <div className="mb-6 rounded-xl border border-slate-200 bg-white shadow-sm overflow-x-auto">
            <div className="px-4 py-3 border-b border-slate-200">
              <p className="text-sm font-semibold text-slate-900">Artwork Type x Level Count</p>
              <p className="text-xs text-slate-500 mt-1">Counts are grouped by artwork type with each level breakdown.</p>
            </div>
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left px-4 py-2 font-semibold text-slate-700">Artwork Type</th>
                  {typeLevelMatrix.levels.map((lv) => (
                    <th key={`matrix-head-${lv}`} className="text-right px-4 py-2 font-semibold text-slate-700">{lv}</th>
                  ))}
                  <th className="text-right px-4 py-2 font-semibold text-slate-900">Total</th>
                </tr>
              </thead>
              <tbody>
                {typeLevelMatrix.typeRows.map((row) => (
                  <tr key={`matrix-row-${row.type}`} className="border-t border-slate-100">
                    <td className="px-4 py-2 text-slate-800 font-medium">{row.type}</td>
                    {typeLevelMatrix.levels.map((lv) => (
                      <td key={`matrix-cell-${row.type}-${lv}`} className="px-4 py-2 text-right text-slate-700">
                        {row[lv] || 0}
                      </td>
                    ))}
                    <td className="px-4 py-2 text-right text-slate-900 font-semibold">{row.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-24">
            <div className="animate-spin rounded-full h-10 w-10 border-2 border-amber-500 border-t-transparent" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center max-w-lg mx-auto space-y-3">
            <p className="text-slate-700 font-medium">No artworks match your search or rights filter.</p>
            {rows.length === 0 && (
              <p className="text-slate-600 text-sm">
                There are still no documents with <code className="text-xs bg-slate-100 px-1">replacementApplied</code>,{' '}
                <code className="text-xs bg-slate-100 px-1">verifiedImageUrl</code>, or{' '}
                <code className="text-xs bg-slate-100 px-1">verifiedImageSha256</code> in project{' '}
                <code className="text-xs bg-slate-100 px-1">{firebaseConfig?.projectId}</code>. Run bulk-apply
                against this same project, or confirm the Firebase console for{' '}
                <code className="text-xs bg-slate-100 px-1">artHistoryArtworks</code>.
              </p>
            )}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {filtered.map((r) => {
                const pb = previewPickerBadge(r);
                return (
                  <div
                    key={r.id}
                    className="text-left rounded-2xl border border-slate-200/80 bg-white shadow-sm hover:shadow-md hover:border-amber-200/80 transition-all overflow-hidden group"
                  >
                    <button
                      type="button"
                      onClick={() => setSelected(r)}
                      className="w-full text-left"
                    >
                    <div className="aspect-[4/3] bg-slate-100 relative">
                      <VerifiedArtworkImage row={r} className="w-full h-full object-contain" />
                      <div className="absolute top-2 left-2 flex items-center gap-1">
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full border border-amber-200 bg-amber-50 text-amber-900">
                          {displayArtworkType(r)}
                        </span>
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full border border-sky-200 bg-sky-50 text-sky-900">
                          {displayLevel(r)}
                        </span>
                      </div>
                    </div>
                    </button>
                    <div className="p-3">
                      <p className="font-medium text-slate-900 line-clamp-2 group-hover:text-amber-900">
                        {displayTitle(r)}
                      </p>
                      <p className="text-sm text-slate-600 line-clamp-1 mt-0.5">
                        {displayArtist(r) || '—'}
                      </p>
                      <p className="text-[11px] text-slate-500 mt-1.5">
                        Preview:{' '}
                        <span className={`font-semibold ${pb.cls}`}>{pb.label}</span>
                        <span className="text-slate-400"> · {pb.dims}</span>
                      </p>
                      <div className="mt-1 flex flex-wrap gap-1">
                        <button
                          type="button"
                          className="text-[10px] px-1.5 py-0.5 rounded border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                          onClick={(e) => {
                            e.stopPropagation();
                            copyToClipboard(
                              `cd Artmosphere/backend && ARTWORK_IDS=${r.id} npm run generate:artwork-previews:fix`
                            );
                          }}
                        >
                          Regenerate (copy cmd)
                        </button>
                      </div>
                      <div className="mt-2 grid grid-cols-2 gap-2" onClick={(e) => e.stopPropagation()}>
                        <select
                          value={displayArtworkType(r) === 'Uncategorized' ? '' : displayArtworkType(r)}
                          onChange={(e) => autoSaveCardFields(r, e.target.value, displayLevel(r))}
                          className="px-2 py-1.5 rounded-lg border border-amber-200 bg-amber-50 text-[11px] text-amber-900"
                        >
                          <option value="">Uncategorized</option>
                          {!CARD_ARTWORK_TYPES.includes(displayArtworkType(r)) && displayArtworkType(r) !== 'Uncategorized' && (
                            <option value={displayArtworkType(r)}>{displayArtworkType(r)}</option>
                          )}
                          {CARD_ARTWORK_TYPES.map((opt) => (
                            <option key={`${r.id}-type-${opt}`} value={opt}>{opt}</option>
                          ))}
                        </select>
                        <select
                          value={CARD_LEVEL_OPTIONS.includes(displayLevel(r)) ? displayLevel(r) : 'Unspecified'}
                          onChange={(e) => autoSaveCardFields(r, displayArtworkType(r), e.target.value)}
                          className="px-2 py-1.5 rounded-lg border border-sky-200 bg-sky-50 text-[11px] text-sky-900"
                        >
                          {CARD_LEVEL_OPTIONS.map((opt) => (
                            <option key={`${r.id}-level-${opt}`} value={opt}>{opt}</option>
                          ))}
                        </select>
                      </div>
                      {(savingById[r.id] || saveErrById[r.id]) && (
                        <p className={`mt-1 text-[11px] ${saveErrById[r.id] ? 'text-red-600' : 'text-emerald-700'}`}>
                          {saveErrById[r.id] ? saveErrById[r.id] : 'Saving…'}
                        </p>
                      )}
                      <p className="text-[11px] text-slate-400 font-mono mt-2 truncate">{r.id}</p>
                      <div className="mt-2 flex justify-end">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            void deleteArtworkRow(r);
                          }}
                          disabled={!!deletingById[r.id]}
                          className="text-[11px] font-semibold px-2.5 py-1 rounded-lg border border-red-200 bg-white text-red-700 hover:bg-red-50 disabled:opacity-50"
                        >
                          {deletingById[r.id] ? 'Deleting…' : 'Delete'}
                        </button>
                      </div>
                      {deleteErrById[r.id] ? (
                        <p className="mt-1 text-[11px] text-red-600">{deleteErrById[r.id]}</p>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>

          </>
        )}
      </div>

      {selected && (
        <DetailModal
          artwork={selected}
          onClose={() => setSelected(null)}
          onSaved={(id, patch) => {
            setRows((prev) => prev.map((row) => (row.id === id ? { ...row, ...patch } : row)));
          }}
          onDeleteArtwork={deleteArtworkRow}
          deleteBusy={!!deletingById[selected.id]}
        />
      )}
    </div>
  );
}

/**
 * Pipeline rows include verifiedImageStoragePath. Resolving via Storage SDK (auth + App Check) is
 * stable after hard refresh; using the stored HTTPS URL first causes hundreds of parallel GETs that
 * often fail cold, leaving thumbnails blank. External-only rows (no storage path) still use imageUrl.
 */
function VerifiedArtworkImage({ row, className }) {
  const primary = primaryImageUrl(row);
  const storagePath =
    typeof row.verifiedImageStoragePath === 'string' ? row.verifiedImageStoragePath.trim() : '';
  const [src, setSrc] = useState('');
  const [showErr, setShowErr] = useState(false);
  /** After SDK URL fails in <img>, try Firestore URL once (token link may still work). */
  const triedHttpFallbackRef = useRef(false);
  const triedProxyFallbackRef = useRef(false);
  const mountedRef = useRef(true);
  const loadedRef = useRef(false);
  const fallbackTimerRef = useRef(null);
  const backendProxy =
    storagePath && storagePath.startsWith('verified-artworks/')
      ? `${backendConfig.baseUrl}/api/verified-artwork-file?path=${encodeURIComponent(storagePath)}`
      : '';

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (fallbackTimerRef.current) {
        clearTimeout(fallbackTimerRef.current);
        fallbackTimerRef.current = null;
      }
    };
  }, []);

  const scheduleFallback = (fn, ms = 2500) => {
    if (fallbackTimerRef.current) clearTimeout(fallbackTimerRef.current);
    fallbackTimerRef.current = setTimeout(() => {
      if (!mountedRef.current || loadedRef.current) return;
      fn();
    }, ms);
  };

  useEffect(() => {
    triedHttpFallbackRef.current = false;
    triedProxyFallbackRef.current = false;
    loadedRef.current = false;
    setShowErr(false);
    if (fallbackTimerRef.current) {
      clearTimeout(fallbackTimerRef.current);
      fallbackTimerRef.current = null;
    }

    if (!primary && !storagePath) {
      setSrc('');
      return undefined;
    }

    // No GCS path (e.g. remote-only): use stored URL directly.
    if (!storagePath) {
      setSrc(primary);
      return undefined;
    }

    // Start with stored Firebase URL for fastest paint.
    // Some deployments return 404 on /api/verified-artwork-file despite valid direct URLs.
    if (primary) {
      setSrc(primary);
      return undefined;
    }
    if (backendProxy) {
      setSrc(backendProxy);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        await warmAuthTokenForStorage(false);
        const url = await getVerifiedObjectDownloadUrl(storagePath);
        if (!cancelled && mountedRef.current) setSrc(url);
      } catch {
        if (cancelled || !mountedRef.current) return;
        if (primary) setSrc(primary);
        else setShowErr(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [row.id, primary, storagePath, backendProxy]);

  const onImgError = () => {
    if (fallbackTimerRef.current) {
      clearTimeout(fallbackTimerRef.current);
      fallbackTimerRef.current = null;
    }
    // CMA sometimes provides TIFF URLs that browsers won't render; fallback to JPEG derivative.
    if (src && /openaccess-cdn\.clevelandart\.org/i.test(src) && /_full\.tiff?(\?|$)/i.test(src)) {
      const jpg = src.replace(/_full\.tiff?/i, '_print.jpg');
      if (jpg !== src) {
        setSrc(jpg);
        return;
      }
    }
    if (!storagePath) {
      setShowErr(true);
      return;
    }
    if (backendProxy && !triedProxyFallbackRef.current && src !== backendProxy) {
      triedProxyFallbackRef.current = true;
      setSrc(backendProxy);
      return;
    }
    if (primary && !triedHttpFallbackRef.current && src !== primary) {
      triedHttpFallbackRef.current = true;
      setSrc(primary);
      return;
    }
    (async () => {
      try {
        await whenSignedIn(15000);
        await warmAuthTokenForStorage(true);
        const url = await getVerifiedObjectDownloadUrl(storagePath);
        if (mountedRef.current) setSrc(url);
      } catch {
        if (mountedRef.current) setShowErr(true);
      }
    })();
  };

  if (!primary && !storagePath) {
    return (
      <div className="w-full h-full flex items-center justify-center text-slate-400 text-sm">No image URL</div>
    );
  }

  if (showErr) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center gap-1 px-3 text-center text-slate-500 text-xs">
        <span>Image did not load</span>
        <span className="text-[10px] text-slate-400 break-all line-clamp-3">
          Check Storage rules for <code className="font-mono">verified-artworks/</code> and bucket{' '}
          <code className="font-mono">{gsVerifiedArtworksBucket().replace(/^gs:\/\//, '')}</code>
        </span>
      </div>
    );
  }

  if (!src) {
    return (
      <div className="w-full h-full flex items-center justify-center text-slate-400 text-xs">Loading…</div>
    );
  }

  return (
    <img
      key={`${row.id}-${src.slice(0, 80)}`}
      src={src}
      alt=""
      className={className}
      loading="lazy"
      decoding="async"
      referrerPolicy="no-referrer"
      onLoad={() => {
        loadedRef.current = true;
        if (fallbackTimerRef.current) {
          clearTimeout(fallbackTimerRef.current);
          fallbackTimerRef.current = null;
        }
      }}
      onError={onImgError}
    />
  );
}

function MetaRow({ label, value, mono }) {
  const v =
    value === undefined || value === null || value === ''
      ? '—'
      : typeof value === 'boolean'
        ? String(value)
        : String(value);
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[200px_1fr] gap-1 sm:gap-3 py-2 border-b border-slate-100 last:border-0">
      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</dt>
      <dd
        className={`text-sm text-slate-800 break-words ${mono ? 'font-mono text-xs' : ''}`}
      >
        {v}
      </dd>
    </div>
  );
}

function DetailModal({ artwork, onClose, onSaved, onDeleteArtwork, deleteBusy }) {
  const [editType, setEditType] = useState(artwork.artworkType || '');
  const [editMovement, setEditMovement] = useState(String(artwork.artMovement || artwork.movement || ''));
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState('');

  useEffect(() => {
    setEditType(artwork.artworkType || '');
    setEditMovement(String(artwork.artMovement || artwork.movement || ''));
    setSaveErr('');
  }, [artwork.id, artwork.artworkType, artwork.artMovement, artwork.movement]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const d = artwork;

  const handleSave = async () => {
    setSaving(true);
    setSaveErr('');
    try {
      const t = editType.trim();
      const m = editMovement.trim();
      if (t && !CARD_ARTWORK_TYPES.includes(t)) {
        setSaveErr('Choose a valid artwork type from the list.');
        return;
      }
      const ref = doc(db, 'artHistoryArtworks', artwork.id);
      const patch = {
        artMovement: m,
        movement: m,
        artworkCategoryReviewNeeded: false,
        movementReviewNeeded: false,
        artworkCategoryReviewedAt: serverTimestamp(),
        artworkCategoryReviewedBy: auth.currentUser?.uid || null,
        movementReviewedAt: serverTimestamp(),
        movementReviewedBy: auth.currentUser?.uid || null,
      };
      if (t) patch.artworkType = t;
      await updateDoc(ref, patch);
      onSaved(artwork.id, {
        ...(t ? { artworkType: t } : {}),
        artMovement: m,
        movement: m,
        artworkCategoryReviewNeeded: false,
        movementReviewNeeded: false,
      });
      onClose();
    } catch (e) {
      setSaveErr(e?.message || String(e));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!onDeleteArtwork) return;
    setSaveErr('');
    const result = await onDeleteArtwork(artwork);
    if (result?.ok) onClose();
    else if (result?.error) setSaveErr(result.error);
  };

  const metadataFields = [
    ['Document ID', d.id],
    ['replacementApplied', d.replacementApplied],
    ['replacementAppliedAt', formatWhen(d.replacementAppliedAt)],
    ['replacementNotes', d.replacementNotes],
    ['verifiedImageStoragePath', d.verifiedImageStoragePath],
    ['verifiedImageCardPreviewStoragePath', d.verifiedImageCardPreviewStoragePath],
    ['verifiedImageCardPreviewGeneratedAt', formatWhen(d.verifiedImageCardPreviewGeneratedAt)],
    ['verifiedImageCardPreviewUrl', d.verifiedImageCardPreviewUrl],
    ['verifiedImageSha256', d.verifiedImageSha256],
    ['verifiedImageUrlRemote', d.verifiedImageUrlRemote],
    ['verifiedArtworkDate', d.verifiedArtworkDate],
    ['verifiedMatchClassification', d.verifiedMatchClassification],
    ['verifiedMatchFinalConfidence', d.verifiedMatchFinalConfidence],
    ['verifiedMatchReason', d.verifiedMatchReason],
    ['rightsStatus', d.rightsStatus],
    ['bulkVerifiedIngest', d.bulkVerifiedIngest],
  ];

  const rightsFields = [
    ['verifiedRightsClassification', d.verifiedRightsClassification],
    ['verifiedLicenseCode', d.verifiedLicenseCode],
    ['verifiedLicenseCategory', d.verifiedLicenseCategory],
    ['verifiedStrictCommercialApproved', d.verifiedStrictCommercialApproved],
    ['verifiedReplacementEligible', d.verifiedReplacementEligible],
    ['verifiedRightsGateReason', d.verifiedRightsGateReason],
    ['verifiedLicense (summary)', d.verifiedLicense],
    ['verifiedRawRightsText', d.verifiedRawRightsText],
    ['verifiedNormalizedRightsText', d.verifiedNormalizedRightsText],
  ];

  return (
    <div
      className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="verified-detail-title"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-t-2xl sm:rounded-2xl shadow-xl max-w-4xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        role="presentation"
      >
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-slate-100">
          <div className="min-w-0">
            <h2 id="verified-detail-title" className="text-lg font-semibold text-slate-900 truncate">
              {displayTitle(d)}
            </h2>
            <p className="text-slate-600 text-sm">{displayArtist(d) || '—'}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-lg px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100"
          >
            Close
          </button>
        </div>
        <div className="overflow-y-auto flex-1 px-5 py-4">
          <div className="flex flex-col md:flex-row gap-6">
            <div className="shrink-0 w-full md:w-72">
              <div className="rounded-xl border border-slate-200 bg-slate-50 overflow-hidden aspect-square flex items-center justify-center relative">
                {needsCategoryVerify(d) && (
                  <span className="absolute top-2 left-2 z-10 text-[10px] font-bold px-2 py-0.5 rounded-full border shadow-sm bg-amber-400 text-amber-950 border-amber-600">
                    Verify
                  </span>
                )}
                <VerifiedArtworkImage row={d} className="max-w-full max-h-full object-contain" />
              </div>
              {d.verifiedSourceUrl && (
                <a
                  href={d.verifiedSourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 inline-block text-sm text-amber-700 hover:underline break-all"
                >
                  Open source record →
                </a>
              )}
            </div>
            <div className="flex-1 min-w-0 space-y-6">
              {needsCategoryVerify(d) && (
                <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950">
                  <p className="font-semibold">Category needs your review</p>
                  <p className="mt-1 text-amber-900/90">
                    The artwork type may have been inferred from the <strong>title</strong> or{' '}
                    <strong>Wikimedia filename</strong>. Confirm the card category (Landscape, Floral, etc.) or
                    change it below, then save.
                  </p>
                  {d.artworkTypeInferenceSource && (
                    <p className="mt-2 text-xs text-amber-800">
                      Inference source:{' '}
                      <code className="bg-white/80 px-1 rounded">
                        {d.artworkTypeInferenceSource === 'artwork_title'
                          ? 'library title / fields'
                          : d.artworkTypeInferenceSource === 'wikimedia_filename'
                            ? 'Wikimedia filename'
                            : String(d.artworkTypeInferenceSource)}
                      </code>
                    </p>
                  )}
                </div>
              )}
              {needsMovementReview(d) && (
                <div className="rounded-xl border border-rose-300 bg-rose-50 px-4 py-3 text-sm text-rose-950">
                  <p className="font-semibold">Movement needs your review</p>
                  <p className="mt-1 text-rose-900/90">
                    This movement came from a lower-confidence source. Please confirm or correct it, then save.
                  </p>
                </div>
              )}
              <section className="rounded-xl border border-slate-200 bg-slate-50/80 p-4 space-y-3">
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-600">
                  Edit artwork type & movement
                </h3>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Artwork type (card category)</label>
                  <select
                    value={editType}
                    onChange={(e) => setEditType(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm"
                  >
                    <option value="">— Select —</option>
                    {CARD_ARTWORK_TYPES.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Art movement</label>
                  <input
                    type="text"
                    value={editMovement}
                    onChange={(e) => setEditMovement(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm"
                    placeholder="e.g. Baroque, Impressionism"
                  />
                </div>
                {saveErr && <p className="text-sm text-red-600">{saveErr}</p>}
                <div className="flex flex-wrap items-center gap-2 pt-1">
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving || deleteBusy}
                    className="px-4 py-2 rounded-lg bg-amber-600 text-white text-sm font-medium hover:bg-amber-700 disabled:opacity-50"
                  >
                    {saving ? 'Saving…' : 'Save changes'}
                  </button>
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={saving || deleteBusy}
                    className="px-4 py-2 rounded-lg border border-slate-200 text-slate-700 text-sm hover:bg-white disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <span className="grow" aria-hidden="true" />
                  <button
                    type="button"
                    onClick={() => void handleDelete()}
                    disabled={saving || deleteBusy}
                    className="px-4 py-2 rounded-lg border border-red-200 bg-white text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
                  >
                    {deleteBusy ? 'Deleting…' : 'Delete artwork'}
                  </button>
                </div>
              </section>
              <section>
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
                  Library & verified labels
                </h3>
                <dl>
                  <MetaRow label="Title (library)" value={d.title || d.artworkTitle} />
                  <MetaRow label="Artist (library)" value={d.artistName} />
                  <MetaRow label="verifiedTitle" value={d.verifiedTitle} />
                  <MetaRow label="verifiedArtist" value={d.verifiedArtist} />
                  <MetaRow label="verifiedPlatform" value={d.verifiedPlatform} />
                  <MetaRow label="verifiedSourceName" value={d.verifiedSourceName} />
                  <MetaRow label="artworkTypeTagSource" value={d.artworkTypeTagSource} />
                  <MetaRow label="artworkCategoryReviewNeeded" value={d.artworkCategoryReviewNeeded} />
                  <MetaRow label="movementTagSource" value={d.movementTagSource} />
                  <MetaRow label="movementReviewNeeded" value={d.movementReviewNeeded} />
                </dl>
              </section>
              <section>
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
                  Rights (strict gate / Firestore)
                </h3>
                <dl>
                  {rightsFields.map(([label, val]) => (
                    <MetaRow key={label} label={label} value={val} mono={label.includes('Text')} />
                  ))}
                </dl>
              </section>
              <section>
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
                  Provenance & pipeline
                </h3>
                <dl>
                  {metadataFields.map(([label, val]) => (
                    <MetaRow key={label} label={label} value={val} mono />
                  ))}
                </dl>
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
